import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useLazyGetResourcesQuery, useGetContentTypeCountsQuery } from "../api/businessApi";
import SEO from "../components/SEO";

const CONTENT_TYPE_LABELS = {
  newsletter: "Newsletters",
  blog: "Blogs",
  video: "Videos",
  guide: "Documents & Forms",
  announcement: "Announcements",
};

const CONTENT_TYPE_ICONS = {
  newsletter: "M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
  blog: "M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z",
  video: "M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z",
  guide: "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
  announcement: "M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z",
};

// File type icons and colors
const FILE_TYPE_CONFIG = {
  "application/pdf": { icon: "📄", label: "PDF", color: "bg-red-100 text-red-700 border-red-200" },
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": { icon: "📊", label: "PPTX", color: "bg-orange-100 text-orange-700 border-orange-200" },
  "application/vnd.ms-powerpoint": { icon: "📊", label: "PPT", color: "bg-orange-100 text-orange-700 border-orange-200" },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": { icon: "📝", label: "DOCX", color: "bg-blue-100 text-blue-700 border-blue-200" },
  "application/msword": { icon: "📝", label: "DOC", color: "bg-blue-100 text-blue-700 border-blue-200" },
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": { icon: "📈", label: "XLSX", color: "bg-green-100 text-green-700 border-green-200" },
  "application/vnd.ms-excel": { icon: "📈", label: "XLS", color: "bg-green-100 text-green-700 border-green-200" },
  "application/zip": { icon: "📦", label: "ZIP", color: "bg-gray-100 text-gray-700 border-gray-200" },
};

const GUIDE_CATEGORIES = [
  "All",
  "Business Templates",
  "Presentations & Charters",
  "Forms & Documents",
];

function getFileConfig(fileType) {
  if (!fileType) return { icon: "📎", label: "File", color: "bg-gray-100 text-gray-700 border-gray-200" };
  return FILE_TYPE_CONFIG[fileType] || { icon: "📎", label: fileType.split("/").pop().toUpperCase(), color: "bg-gray-100 text-gray-700 border-gray-200" };
}

function formatFileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

const ResourcesList = () => {
  const { type } = useParams();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [guideCategory, setGuideCategory] = useState("All");

  const contentType = type ? type.replace(/s$/, "") : null;
  const isGuideView = contentType === "guide";

  const [getResources, { data, isLoading }] = useLazyGetResourcesQuery();
  const { data: countsData } = useGetContentTypeCountsQuery();

  useEffect(() => {
    const params = { page, limit: 12, status: "published" };
    if (contentType) params.content_type = contentType;
    if (search) params.search = search;
    if (isGuideView && guideCategory !== "All") params.category = guideCategory;
    getResources(params);
  }, [page, contentType, search, guideCategory, getResources, isGuideView]);

  const resources = data?.resources || [];
  const pagination = data?.pagination || {};
  const counts = countsData?.counts || [];

  const pageTitle = contentType
    ? CONTENT_TYPE_LABELS[contentType] || "Resources"
    : "All Resources";

  const isImage = (url) => url && /\.(jpg|jpeg|png|gif|webp|svg)/i.test(url);

  // Download card — single click to download
  const DownloadCard = ({ item }) => {
    const fileConfig = getFileConfig(item.file_type);
    const CardWrapper = item.file_url ? 'a' : 'div';
    const cardProps = item.file_url ? {
      href: item.file_url,
      download: true,
      target: "_blank",
      rel: "noopener noreferrer",
    } : {};

    return (
      <CardWrapper
        {...cardProps}
        className="group flex items-center gap-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5 hover:shadow-md hover:border-[#135879]/30 transition-all duration-200 cursor-pointer"
      >
        {/* File type icon */}
        <div className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-lg ${fileConfig.color} border flex items-center justify-center text-xl sm:text-2xl`}>
          {fileConfig.icon}
        </div>

        {/* Title & meta */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-1 group-hover:text-[#135879] transition-colors">
            {item.title}
          </h3>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
            <span className={`font-semibold px-1.5 py-0.5 rounded ${fileConfig.color} border`}>
              {fileConfig.label}
            </span>
            {item.file_size > 0 && <span>{formatFileSize(item.file_size)}</span>}
            {item.category && <span className="hidden sm:inline">{item.category}</span>}
          </div>
        </div>

        {/* Download icon */}
        {item.file_url ? (
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#135879] text-white flex items-center justify-center group-hover:bg-[#0e4562] transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </div>
        ) : (
          <span className="text-xs text-gray-300 italic">Coming soon</span>
        )}
      </CardWrapper>
    );
  };

  // Standard article card
  const ArticleCard = ({ item }) => (
    <Link
      key={item.id}
      to={item.slug ? `/resource/${item.slug}` : `/newsletter/${item.newsletter_id}`}
      className="group bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
    >
      <div className="aspect-video bg-gray-100 overflow-hidden">
        {item.featured_image && isImage(item.featured_image) ? (
          <img
            src={item.featured_image}
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#135879]/10 to-[#135879]/5">
            <svg className="w-12 h-12 text-[#135879]/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d={CONTENT_TYPE_ICONS[item.content_type] || CONTENT_TYPE_ICONS.blog} />
            </svg>
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-medium px-2 py-1 rounded-full bg-[#135879]/10 text-[#135879]">
            {CONTENT_TYPE_LABELS[item.content_type] || item.content_type}
          </span>
          {item.category && (
            <span className="text-xs text-gray-500">{item.category}</span>
          )}
        </div>
        <h3 className="font-semibold text-gray-900 text-lg mb-2 line-clamp-2 group-hover:text-[#135879] transition-colors">
          {item.title}
        </h3>
        {item.excerpt && (
          <p className="text-gray-600 text-sm line-clamp-2 mb-3">{item.excerpt}</p>
        )}
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>{item.author}</span>
          <span>
            {item.publish_date
              ? new Date(item.publish_date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
              : ""}
          </span>
        </div>
      </div>
    </Link>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO title={pageTitle} />
      {/* Hero */}
      <div className="bg-gradient-to-r from-[#135879] to-[#0e4562] text-white py-10 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3">{pageTitle}</h1>
          <p className="text-base sm:text-lg text-white/80">
            {isGuideView
              ? "Download business templates, forms, and reference documents"
              : "Explore our collection of resources to help grow your business"}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Type Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 sm:mb-8 overflow-x-auto pb-2">
          <Link
            to="/resources"
            className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition whitespace-nowrap ${
              !type
                ? "bg-[#135879] text-white"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            }`}
          >
            All
          </Link>
          {Object.entries(CONTENT_TYPE_LABELS).map(([key, label]) => {
            const count = counts.find((c) => c.content_type === key)?.count || 0;
            return (
              <Link
                key={key}
                to={`/resources/${key}s`}
                className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition flex items-center gap-1.5 sm:gap-2 whitespace-nowrap ${
                  contentType === key
                    ? "bg-[#135879] text-white"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                }`}
              >
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={CONTENT_TYPE_ICONS[key]} />
                </svg>
                {label}
                {count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    contentType === key ? "bg-white/20" : "bg-gray-100"
                  }`}>
                    {count}
                  </span>
                )}
              </Link>
            );
          })}
        </div>

        {/* Guide category filters (only on guide view) */}
        {isGuideView && (
          <div className="flex flex-wrap gap-2 mb-6">
            {GUIDE_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => { setGuideCategory(cat); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition ${
                  guideCategory === cat
                    ? "bg-[#d4af37] text-black"
                    : "bg-white text-gray-600 border border-gray-200 hover:border-[#d4af37] hover:text-[#d4af37]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder={isGuideView ? "Search templates & documents..." : "Search resources..."}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#135879] focus:border-transparent"
            />
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-[#135879] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : resources.length === 0 ? (
          <div className="text-center py-20">
            <svg className="w-20 h-20 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d={isGuideView
                ? "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                : "M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2"
              } />
            </svg>
            <p className="text-xl font-medium text-gray-600">
              {isGuideView ? "No documents or forms uploaded yet" : "No resources found"}
            </p>
            <p className="text-gray-400 mt-1">Check back later for new content</p>
          </div>
        ) : isGuideView ? (
          /* Single column list for guides — clean, scannable */
          <div className="flex flex-col gap-3">
            {resources.map((item) => (
              <DownloadCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          /* Mixed view: download rows for file items, article cards for the rest */
          <>
            {/* Download items (guides with files) shown as a compact list at top */}
            {resources.some((item) => item.file_url) && (
              <div className="flex flex-col gap-3 mb-6">
                {resources
                  .filter((item) => item.file_url)
                  .map((item) => (
                    <DownloadCard key={item.id} item={item} />
                  ))}
              </div>
            )}
            {/* Standard article cards for non-file items */}
            {resources.some((item) => !item.file_url) && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {resources
                  .filter((item) => !item.file_url)
                  .map((item) => (
                    <ArticleCard key={item.id} item={item} />
                  ))}
              </div>
            )}
          </>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-10">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
              disabled={page >= pagination.totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourcesList;
